import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import threadService from '../../services/threadService';
import FeedbackDialog from './FeedbackDialog';

const MessageActions = ({ message, threadId, theme, previousUserMessage, onFeedbackChange, showRetry, onRetry, isRetrying, isLastMessage }) => {
  const [copied, setCopied] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const copyTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const currentRating = message.feedback?.rating || null;

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('[MessageActions] Copy failed:', err);
    }
  }, [message.content]);

  const submitFeedback = useCallback(async (rating, extra = {}) => {
    setIsSubmitting(true);
    try {
      await threadService.submitFeedback(threadId, message.id, {
        rating,
        category: extra.category || null,
        comment: extra.comment || null,
        messageContentSnapshot: message.content,
        userMessageContent: previousUserMessage || null,
        agentName: message.agentName || null
      });
      onFeedbackChange(message.id, { rating, category: extra.category, comment: extra.comment });
    } catch (err) {
      console.error('[MessageActions] Submit feedback failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [threadId, message.id, message.content, message.agentName, previousUserMessage, onFeedbackChange]);

  const removeFeedback = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await threadService.removeFeedback(threadId, message.id);
      onFeedbackChange(message.id, null);
    } catch (err) {
      console.error('[MessageActions] Remove feedback failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [threadId, message.id, onFeedbackChange]);

  const handleThumbUp = useCallback(() => {
    if (currentRating === 'positive') {
      removeFeedback();
    } else {
      submitFeedback('positive');
    }
  }, [currentRating, removeFeedback, submitFeedback]);

  const handleThumbDown = useCallback(() => {
    if (currentRating === 'negative') {
      removeFeedback();
    } else {
      setDialogOpen(true);
    }
  }, [currentRating, removeFeedback]);

  const handleDialogSubmit = useCallback(({ category, comment }) => {
    submitFeedback('negative', { category, comment });
    setDialogOpen(false);
  }, [submitFeedback]);

  const hasFeedback = currentRating !== null;

  return (
    <>
      <Box
        className="message-actions"
        sx={{
          display: 'flex',
          gap: 0.25,
          mt: 0.25,
          ml: 0.5,
          opacity: (hasFeedback || showRetry || isLastMessage) ? 0.7 : 0,
          transition: 'opacity 0.15s ease-in-out',
          '@media (hover: none)': {
            opacity: 0.7,
          }
        }}
      >
        <Tooltip title={copied ? 'Copiado' : 'Copiar'} arrow placement="top">
          <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }}>
            {copied ? (
              <CheckIcon sx={{ fontSize: '1rem', color: 'text.primary' }} />
            ) : (
              <ContentCopyIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title={currentRating === 'positive' ? 'Quitar valoracion' : 'Buena respuesta'} arrow placement="top">
          <span>
            <IconButton size="small" onClick={handleThumbUp} disabled={isSubmitting} sx={{ p: 0.5 }}>
              {currentRating === 'positive' ? (
                <ThumbUpIcon sx={{ fontSize: '1rem', color: 'text.primary' }} />
              ) : (
                <ThumbUpOutlinedIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
              )}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={currentRating === 'negative' ? 'Quitar valoracion' : 'Mala respuesta'} arrow placement="top">
          <span>
            <IconButton size="small" onClick={handleThumbDown} disabled={isSubmitting} sx={{ p: 0.5 }}>
              {currentRating === 'negative' ? (
                <ThumbDownIcon sx={{ fontSize: '1rem', color: 'text.primary' }} />
              ) : (
                <ThumbDownOutlinedIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
              )}
            </IconButton>
          </span>
        </Tooltip>

        {showRetry && (
          <>
            <Box sx={{ width: '1px', height: 16, bgcolor: 'divider', mx: 0.5, alignSelf: 'center' }} />
            <Tooltip title={isRetrying ? 'Reintentando...' : 'Reintentar'} arrow placement="top">
              <span>
                <IconButton size="small" onClick={onRetry} disabled={isRetrying} sx={{ p: 0.5 }}>
                  {isRetrying ? (
                    <CircularProgress size={14} sx={{ color: 'text.secondary' }} />
                  ) : (
                    <RefreshIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Box>

      <FeedbackDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleDialogSubmit}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default MessageActions;
